import {
  Add,
  Close,
  Delete,
  Folder,
  Settings,
  Upload,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useState } from "react";
import DepartmentSelector from "../../../components/DepartmentSelector";
import LoadingButton from "../../../components/LoadingButton";
import PopOutTextInput from "../../../components/PopOutTextInput";
import TagsAutocomplete from "../../../components/TagsAutocomplete";
import VoiceSelector from "../../../components/VoiceSelector";
import { DEFAULT_MODEL } from "../../../constants";
import { Employee, PublicFolderShare } from "../../../types";
import { hostname } from "../../../utils";
import { useWhitelabel } from "../../../context/WhitelabelContext";
import { useEmployeeTerminology } from "../../../utils/terminology";
import ChatSettingsDialog from "../../chat/ChatSettingsDialog";
import PublicFolderSelector from "./PublicFolderSelector";

interface EditEmployeeDialogProps {
  open: boolean;
  onClose: () => void;
  employee: Employee | null;
  editMode: boolean;
  onSave: (employee: Employee, publicSettings: any) => Promise<void>;
  onUpdatePhoto: () => void;
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  photoLoading: boolean;
  uploadLoading: boolean;
  modalLoading: boolean;
  companyId: string;
  companyName: string;
  getToken: () => Promise<string>;
  currentCompanyPlan?: string;
  eventListener?: (event: any) => void;
}

const EditEmployeeDialog: React.FC<EditEmployeeDialogProps> = ({
  open,
  onClose,
  employee,
  editMode,
  onSave,
  onUpdatePhoto,
  onFileUpload,
  photoLoading,
  uploadLoading,
  modalLoading,
  companyId,
  companyName,
  getToken,
  currentCompanyPlan,
  eventListener,
}) => {
  const { employee: employeeText, employeeLower } = useEmployeeTerminology();
  const { whitelabelSettings } = useWhitelabel();
  const publicEmployeesEnabled = whitelabelSettings?.publicEmployeeFilter !== "none";
  const [currentTab, setCurrentTab] = useState(0);
  const [formEmployee, setFormEmployee] = useState<Employee>(
    employee || {
      uid: "",
      id: "",
      title: "",
      name: "",
      prompt: "",
      companyId: "",
      tags: [],
      picture: "",
      public: false,
    }
  );
  const [publicSettings, setPublicSettings] = useState({
    contactEmail: employee?.contactEmail || "",
    ctaText: employee?.ctaText || "",
  });
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>(
    []
  );
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [chatSettingsDialogOpen, setChatSettingsDialogOpen] = useState(false);
  // Public folders are managed as form state and saved with the employee
  const [attachedPublicFolders, setAttachedPublicFolders] = useState<
    PublicFolderShare[]
  >([]);
  const [folderSelectorOpen, setFolderSelectorOpen] = useState(false);

  React.useEffect(() => {
    if (employee) {
      setFormEmployee(employee);
      setPublicSettings({
        contactEmail: employee.contactEmail || "",
        ctaText: employee.ctaText || "",
      });
      setCurrentTab(0);
      setAttachedPublicFolders([]);

      // Load employee departments if editing
      if (editMode && employee.id) {
        loadEmployeeDepartments(employee.id);
        // Load public folder details if employee has attached folders
        if (employee.public && employee.publicFolderIds?.length) {
          loadPublicFolderDetails(employee.publicFolderIds);
        }
      }
    }
  }, [employee]);

  const loadEmployeeDepartments = async (employeeId: string) => {
    setLoadingDepartments(true);
    const currentToken = await getToken();

    try {
      const response = await axios.get(
        `${hostname}/employees/${employeeId}/departments?companyId=${companyId}`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
      setSelectedDepartmentIds(response.data.map((dept: any) => dept.id));
    } catch (err: any) {
      // Silently fail - departments are optional
      console.error("Failed to load employee departments:", err);
    } finally {
      setLoadingDepartments(false);
    }
  };

  const loadPublicFolderDetails = async (folderIds: string[]) => {
    const currentToken = await getToken();

    try {
      // Fetch all public folders and filter to the ones we need
      const response = await axios.get(
        `${hostname}/folder-shares/public?per_page=100`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
      const allFolders: PublicFolderShare[] = response.data.shares || [];
      // Filter to only the folders attached to this employee
      const attachedFolders = allFolders.filter((f) =>
        folderIds.includes(f.id)
      );
      setAttachedPublicFolders(attachedFolders);
    } catch (err: any) {
      console.error("Failed to load public folder details:", err);
    }
  };

  const handleAddPublicFolder = (folder: PublicFolderShare) => {
    // Check if already in list
    if (attachedPublicFolders.some((f) => f.id === folder.id)) {
      return;
    }
    setAttachedPublicFolders([...attachedPublicFolders, folder]);
    setFolderSelectorOpen(false);
  };

  const handleRemovePublicFolder = (folderShareId: string) => {
    setAttachedPublicFolders(
      attachedPublicFolders.filter((f) => f.id !== folderShareId)
    );
  };

  const handleSave = async () => {
    // Include departmentIds and publicFolderIds in the employee data
    const employeeWithExtras = {
      ...formEmployee,
      departmentIds: selectedDepartmentIds,
      // Only include publicFolderIds if employee is public
      ...(formEmployee.public && {
        publicFolderIds: attachedPublicFolders.map((f) => f.id),
      }),
    };

    await onSave(employeeWithExtras, publicSettings);
  };

  const handleClose = () => {
    setCurrentTab(0);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="edit-employee-dialog-title"
    >
      <DialogTitle>
        {!editMode ? `Add New ${employeeText}` : `Edit ${employeeText}`}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {!editMode && (
          <div>
            <Typography style={{ marginTop: 10 }}>
              Create an {employeeLower} by filling in the details below. Leave
              fields blank to auto-generate them with AI.
            </Typography>

            <Box sx={{ mt: 2, mb: 2 }}>
              <Avatar
                sx={{ height: 80, width: 80, mb: 1 }}
                src={formEmployee.picture}
              />
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <input
                  type="file"
                  accept="image/*"
                  onChange={onFileUpload}
                  style={{ display: "none" }}
                  id="employee-photo-upload-create"
                />
                <label htmlFor="employee-photo-upload-create">
                  <LoadingButton
                    variant="outlined"
                    component="span"
                    loading={uploadLoading}
                    disabled={photoLoading}
                    startIcon={<Upload />}
                  >
                    Upload Photo (Optional)
                  </LoadingButton>
                </label>
                <Typography variant="caption" color="text.secondary">
                  Leave blank to auto-generate
                </Typography>
              </Box>
            </Box>

            <TextField
              style={{ marginTop: 20 }}
              fullWidth
              value={formEmployee.name}
              label="Name (Optional)"
              placeholder="Leave blank to auto-generate"
              onChange={(e) =>
                setFormEmployee({ ...formEmployee, name: e.target.value })
              }
            />

            <TextField
              style={{ marginTop: 20 }}
              fullWidth
              required
              value={formEmployee.title}
              label="Job Title (Required)"
              onChange={(e) =>
                setFormEmployee({ ...formEmployee, title: e.target.value })
              }
            />

            <TextField
              style={{ marginTop: 20 }}
              fullWidth
              multiline
              minRows={3}
              value={formEmployee.prompt}
              label="System Prompt (Optional)"
              placeholder="Leave blank to auto-generate"
              onChange={(e) =>
                setFormEmployee({ ...formEmployee, prompt: e.target.value })
              }
            />

            <TextField
              style={{ marginTop: 20 }}
              fullWidth
              multiline
              minRows={3}
              value={formEmployee.responseInstructions || ""}
              label="Response Instructions (Optional)"
              placeholder={`Specific instructions for how this ${employeeLower} should respond to questions...`}
              helperText={`These instructions will be added to the system prompt to guide how the AI ${employeeLower} responds`}
              onChange={(e) =>
                setFormEmployee({
                  ...formEmployee,
                  responseInstructions: e.target.value,
                })
              }
            />

            <TagsAutocomplete
              value={formEmployee.tags}
              onChange={(tags) => setFormEmployee({ ...formEmployee, tags })}
              companyId={companyId}
              getToken={getToken}
              style={{ marginTop: 20 }}
            />
            <DepartmentSelector
              companyId={companyId}
              getToken={getToken}
              selectedDepartmentIds={selectedDepartmentIds}
              onChange={setSelectedDepartmentIds}
              label="Departments (Optional)"
              helperText={`Assign this ${employeeLower} to specific departments to control which collaborators can access them`}
              style={{ marginTop: 20 }}
            />
          </div>
        )}

        {editMode && (
          <div>
            <Tabs
              value={currentTab}
              onChange={(e, newValue) => setCurrentTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
            >
              <Tab label="General" />
              <Tab label="Chat Settings" />
              {publicEmployeesEnabled && <Tab label="Public Settings" />}
            </Tabs>

            {/* Container with fixed height to prevent jumping */}
            <Box sx={{ height: "600px", overflow: "auto" }}>
              {/* General Tab */}
              {currentTab === 0 && (
                <div>
                  <Avatar
                    sx={{ height: 80, width: 80, mt: 2 }}
                    src={formEmployee.picture}
                  />
                  <Box
                    sx={{
                      mt: 1,
                      display: "flex",
                      gap: 1,
                      flexDirection: "column",
                      alignItems: "flex-start",
                    }}
                  >
                    <LoadingButton
                      variant="outlined"
                      loading={photoLoading}
                      onClick={onUpdatePhoto}
                      disabled={uploadLoading}
                    >
                      Generate Photo (2 credits)
                    </LoadingButton>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={onFileUpload}
                      style={{ display: "none" }}
                      id="employee-photo-upload"
                    />
                    <label htmlFor="employee-photo-upload">
                      <LoadingButton
                        variant="outlined"
                        component="span"
                        loading={uploadLoading}
                        disabled={photoLoading}
                        startIcon={<Upload />}
                      >
                        Upload Custom Photo
                      </LoadingButton>
                    </label>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      Upload PNG, JPG up to 2MB
                    </Typography>
                  </Box>
                  <TextField
                    style={{ marginTop: 20 }}
                    fullWidth
                    value={formEmployee.name}
                    label="Name"
                    onChange={(e: any) => {
                      e.stopPropagation();
                      setFormEmployee({
                        ...formEmployee,
                        name: e.target.value,
                      });
                    }}
                  />
                  <TextField
                    style={{ marginTop: 20 }}
                    fullWidth
                    value={formEmployee.title}
                    label="Job Title"
                    onChange={(e) =>
                      setFormEmployee({
                        ...formEmployee,
                        title: e.target.value,
                      })
                    }
                  />
                  <TextField
                    style={{ marginTop: 20 }}
                    fullWidth
                    multiline
                    minRows={2}
                    value={formEmployee.description || ""}
                    label="Description"
                    placeholder={`Briefly describe what this ${employeeLower} can help with...`}
                    helperText={`A friendly description of what this ${employeeLower} can do. This is for display only and not used by the AI.`}
                    onChange={(e) =>
                      setFormEmployee({
                        ...formEmployee,
                        description: e.target.value,
                      })
                    }
                  />
                  <PopOutTextInput
                    style={{ marginTop: 20 }}
                    fullWidth
                    multiline
                    rows={5}
                    maxRows={5}
                    value={formEmployee.prompt}
                    label="System Prompt"
                    onChange={(value) =>
                      setFormEmployee({ ...formEmployee, prompt: value })
                    }
                    dialogTitle="Edit System Prompt"
                    popOutRows={20}
                    aiContext={`This is a system prompt for an AI ${employeeLower}/assistant. Define the ${employeeLower}'s role, personality, expertise areas, and how they should respond to users.`}
                    getToken={getToken}
                    companyId={companyId}
                  />
                  <PopOutTextInput
                    style={{ marginTop: 20 }}
                    fullWidth
                    multiline
                    rows={4}
                    maxRows={4}
                    value={formEmployee.responseInstructions || ""}
                    label="Response Instructions (Optional)"
                    placeholder={`Specific instructions for how this ${employeeLower} should respond to questions...`}
                    helperText={`These instructions will be added to the system prompt to guide how the AI ${employeeLower} responds`}
                    onChange={(value) =>
                      setFormEmployee({
                        ...formEmployee,
                        responseInstructions: value,
                      })
                    }
                    dialogTitle="Edit Response Instructions"
                    dialogDescription={`These instructions will be added to the system prompt to guide how the AI ${employeeLower} responds to questions.`}
                    popOutRows={20}
                    aiContext={`These are response instructions for an AI ${employeeLower}. Include guidance on tone, formatting, length preferences, and any specific behaviors for different types of questions.`}
                    getToken={getToken}
                    companyId={companyId}
                  />
                  <TagsAutocomplete
                    value={formEmployee.tags}
                    onChange={(tags) =>
                      setFormEmployee({ ...formEmployee, tags })
                    }
                    companyId={companyId}
                    getToken={getToken}
                    style={{ marginTop: 20 }}
                  />
                  <DepartmentSelector
                    companyId={companyId}
                    getToken={getToken}
                    selectedDepartmentIds={selectedDepartmentIds}
                    onChange={setSelectedDepartmentIds}
                    label="Departments (Optional)"
                    helperText={`Assign this ${employeeLower} to specific departments to control which collaborators can access them`}
                    style={{ marginTop: 20 }}
                  />
                </div>
              )}

              {/* Chat Settings Tab */}
              {currentTab === 1 && (
                <div>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Greeting Message
                  </Typography>

                  <TextField
                    fullWidth
                    value={formEmployee.greetingMessage || ""}
                    label="Greeting Message (Optional)"
                    placeholder="Hello! How can I help you?"
                    helperText="Custom greeting message shown when starting a new chat. Leave blank for default."
                    onChange={(e) =>
                      setFormEmployee({
                        ...formEmployee,
                        greetingMessage: e.target.value,
                      })
                    }
                    sx={{ mb: 4 }}
                  />

                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Voice Settings
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    Select a default ElevenLabs voice for this {employeeLower}. This voice will be used for voice conversations unless overridden in the chat settings.
                  </Typography>

                  <VoiceSelector
                    value={formEmployee.voiceId || null}
                    onChange={(voiceId) =>
                      setFormEmployee({
                        ...formEmployee,
                        voiceId: voiceId || undefined,
                      })
                    }
                    companyId={companyId}
                    getToken={getToken}
                    label="Default Voice (Optional)"
                    helperText="Leave blank to use the system default voice"
                    showTestButton={true}
                    eventListener={eventListener}
                    sx={{ mb: 4 }}
                    valueType="voiceId"
                  />

                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Default Chat Settings
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    Configure default chat settings for this {employeeLower}.
                    When users start a new chat with this {employeeLower}, these
                    settings will be used by default.
                  </Typography>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!formEmployee.chatSettings}
                        onChange={(e) => {
                          if (e.target.checked) {
                            // Initialize with default settings
                            setFormEmployee({
                              ...formEmployee,
                              chatSettings: {
                                model: DEFAULT_MODEL,
                                documents: true,
                                searchEngine: false,
                                company: true,
                                employee: true,
                                documentStrategy: "similarity-ranking",
                                documentScope: "/shared",
                                documentPath: "/",
                                longTermMemories: false,
                                shortTermMemories: false,
                                responseInstructions: true,
                                websitesJs: false,
                                news: false,
                                strategy: "one-shot",
                              },
                            });
                          } else {
                            // Clear settings
                            setFormEmployee({
                              ...formEmployee,
                              chatSettings: null,
                            });
                          }
                        }}
                      />
                    }
                    label={`Use custom chat settings for this ${employeeLower}`}
                    sx={{ mb: 3 }}
                  />

                  {formEmployee.chatSettings && (
                    <Box sx={{ ml: 4 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Custom chat settings are enabled. Click the button below
                        to configure the settings.
                      </Typography>

                      <Button
                        variant="outlined"
                        startIcon={<Settings />}
                        onClick={() => setChatSettingsDialogOpen(true)}
                      >
                        Configure Chat Settings
                      </Button>
                    </Box>
                  )}

                  {!formEmployee.chatSettings && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 4 }}
                    >
                      When disabled, new chats will use company-wide default
                      settings.
                    </Typography>
                  )}

                  <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Settings Access
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formEmployee.isSettingsLocked || false}
                          onChange={(e) =>
                            setFormEmployee({
                              ...formEmployee,
                              isSettingsLocked: e.target.checked,
                            })
                          }
                        />
                      }
                      label={`Lock chat settings for non-admin users`}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1, ml: 4 }}
                    >
                      When enabled, only admins and owners can modify chat settings for this {employeeLower}. Regular members will see settings as read-only.
                    </Typography>
                  </Box>
                </div>
              )}

              {/* Public Settings Tab */}
              {currentTab === 2 && (
                <div>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Public {employeeText} Settings
                  </Typography>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={formEmployee.public || false}
                        onChange={(e) =>
                          setFormEmployee({
                            ...formEmployee,
                            public: e.target.checked,
                          })
                        }
                      />
                    }
                    label={`Make this ${employeeLower} publicly available`}
                    sx={{ mb: 3 }}
                  />

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    When enabled, other companies can discover and add your{" "}
                    {employeeLower} to their workspace. You'll receive quote
                    requests when companies want to hire your services.
                  </Typography>

                  <TextField
                    fullWidth
                    margin="dense"
                    label="Contact Email"
                    type="email"
                    value={publicSettings.contactEmail}
                    onChange={(e) =>
                      setPublicSettings({
                        ...publicSettings,
                        contactEmail: e.target.value,
                      })
                    }
                    placeholder="Enter the email where you want to receive quote requests"
                    helperText={`This email will be used to contact you when someone requests a quote for this ${employeeLower}`}
                    sx={{ mb: 2 }}
                    disabled={!formEmployee.public}
                    required={formEmployee.public}
                  />

                  <TextField
                    fullWidth
                    margin="dense"
                    label="Call-to-Action Button Text (Optional)"
                    value={publicSettings.ctaText}
                    onChange={(e) =>
                      setPublicSettings({
                        ...publicSettings,
                        ctaText: e.target.value,
                      })
                    }
                    placeholder="e.g., 'Email for Quote', 'Get Quote', 'Contact Us'"
                    helperText="If provided, a button with this text will appear on the chat. Leave blank to hide the button."
                    disabled={!formEmployee.public}
                  />

                  {/* Attached Public Folders Section */}
                  {formEmployee.public && editMode && (
                    <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: "divider" }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Attached Knowledge Base Folders
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Attach public knowledge base folders to this{" "}
                        {employeeLower}. When other companies use this public{" "}
                        {employeeLower}, it will automatically search the attached
                        folders for relevant information to answer questions.
                      </Typography>

                      <Box
                        sx={{
                          mb: 3,
                          p: 2,
                          bgcolor: "action.hover",
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ mb: 1 }}
                        >
                          How Public Folders Work
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          component="div"
                        >
                          <ul style={{ margin: 0, paddingLeft: 20 }}>
                            <li>
                              Public folders allow your {employeeLower} to access
                              shared knowledge when used by other companies
                            </li>
                            <li>
                              To create a public folder, go to{" "}
                              <strong>Knowledge Base</strong>, right-click a folder,
                              and select <strong>Share Folder</strong>, then choose{" "}
                              <strong>Make Public</strong>
                            </li>
                            <li>
                              Only folders you've made public will appear in the
                              list below
                            </li>
                            <li>
                              Documents in attached folders are automatically
                              searched when users ask questions
                            </li>
                          </ul>
                        </Typography>
                      </Box>

                      {attachedPublicFolders.length > 0 ? (
                        <List dense>
                          {attachedPublicFolders.map((folder) => (
                            <ListItem
                              key={folder.id}
                              sx={{
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 1,
                                mb: 1,
                              }}
                            >
                              <ListItemIcon>
                                <Folder color="primary" />
                              </ListItemIcon>
                              <ListItemText
                                primary={folder.folderName || folder.folderPath}
                                secondary={
                                  folder.ownerCompany
                                    ? `From: ${folder.ownerCompany.name}`
                                    : folder.description || folder.folderPath
                                }
                              />
                              <ListItemSecondaryAction>
                                <IconButton
                                  edge="end"
                                  onClick={() =>
                                    handleRemovePublicFolder(folder.id)
                                  }
                                  size="small"
                                >
                                  <Delete />
                                </IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2, fontStyle: "italic" }}
                        >
                          No folders attached yet. Click "Add Public Folder" to
                          attach knowledge base folders that this {employeeLower}{" "}
                          can reference when answering questions.
                        </Typography>
                      )}

                      <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => setFolderSelectorOpen(true)}
                      >
                        Add Public Folder
                      </Button>
                    </Box>
                  )}
                </div>
              )}
            </Box>
          </div>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between" }}>
        {!editMode ? (
          <Chip label="3 credits" size="small" variant="outlined" />
        ) : (
          <Box />
        )}
        <Box>
          <Button onClick={handleClose}>Cancel</Button>
          <LoadingButton
            loading={modalLoading}
            variant="contained"
            onClick={handleSave}
            disabled={!editMode && !formEmployee.title}
          >
            {!editMode ? "Create" : "Save"}
          </LoadingButton>
        </Box>
      </DialogActions>

      {/* Chat Settings Dialog */}
      {chatSettingsDialogOpen && (
        <ChatSettingsDialog
          open={chatSettingsDialogOpen}
          onClose={() => setChatSettingsDialogOpen(false)}
          contextSettings={formEmployee.chatSettings || {}}
          setContextSettings={(settings: any) => {
            setFormEmployee({ ...formEmployee, chatSettings: settings });
          }}
          strategyOptions={[]}
          saveCompanyDefaultSettings={() => {}}
          saveSettings={() => {
            setChatSettingsDialogOpen(false);
          }}
          currentPlan={currentCompanyPlan || null}
          company={{ id: companyId, name: companyName }}
          getToken={getToken}
          eventListener={eventListener}
        />
      )}

      {/* Public Folder Selector Dialog */}
      {folderSelectorOpen && (
        <PublicFolderSelector
          open={folderSelectorOpen}
          onClose={() => setFolderSelectorOpen(false)}
          onSelect={handleAddPublicFolder}
          excludedFolderIds={attachedPublicFolders.map((f) => f.id)}
          getToken={getToken}
        />
      )}
    </Dialog>
  );
};

export default EditEmployeeDialog;
